import React, { useState, useEffect } from 'react';
import './Booking.css';
import axios from "axios";

// Font Awesome icons
import { FaUser, FaEnvelope, FaPhone, FaCalendarCheck, FaCalendar, FaUsers, FaDollarSign, FaComment, FaAngleDown } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';
import { Heading } from '../../../components';

// API endpoint for submitting the booking request
const API_PATH = 'https://eternalvows-react.asdesignsgalaxy.com/api/phpmailer/booking.php';

// Regular expressions for validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const BookingForm = () => {

  // State for form input fields
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    eventType: '',
    date: '',
    eventLocation: '',
    guests: '',
    budget: '',
    additionalNotes: ''
  });

  // Form state tracking
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Hide success message after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  // Handle form submission
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];

      // Validate required fields
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.eventType) validationErrors.push('Event type is required');
      if (!formData.date) validationErrors.push('Date is required');
      if (!formData.eventLocation) validationErrors.push('Event location is required');
      if (!formData.guests) validationErrors.push('Number of guests is required');
      if (!formData.budget) validationErrors.push('Estimated budget is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      // Submit to backend
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Booking request failed');
      }

      // Reset form on success
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        eventType: '',
        date: '',
        eventLocation: '',
        guests: '',
        budget: '',
        additionalNotes: ''
      });

    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }

  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section className="custom-form booking linear-bg">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={'Request a Wedding Booking'} subTitle={'Plan Your Wedding'} sideHeading linear />

            <div className="input-box">

              {/* Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Phone Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="number"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Event Type Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="eventType"><FaCalendarCheck /></label>
                  <select
                    className="select-box"
                    id="eventType"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Event Type</option>
                    <option value="Wedding Ceremony">Wedding Ceremony</option>
                    <option value="Wedding Reception">Wedding Reception</option>
                    <option value="Engagement Party">Engagement Party</option>
                    <option value="other">Other</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown className="icon" />
                  </div>
                </div>
              </div>
            </div>

            <div className="input-box">

              {/* Date Input Field */}
              <div className="input-field">
                <label htmlFor="date"><FaCalendar /></label>
                <input
                  type="text"
                  name="date"
                  placeholder="Date"
                  id="date"
                  className="box"
                  value={formData.date}
                  onChange={handleInputChange}
                  onFocus={(e) => e.target.type = 'date'}
                  onBlur={(e) => e.target.type = 'text'}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Event Location Input Field */}
              <div className="input-field">
                <label htmlFor="eventLocation"><FaLocationDot /></label>
                <input
                  type="text"
                  name="eventLocation"
                  className="box"
                  id="eventLocation"
                  placeholder="Event Location"
                  value={formData.eventLocation}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Number of Guests Input Field */}
              <div className="input-field">
                <label htmlFor="guests"><FaUsers /></label>
                <input
                  type="number"
                  name="guests"
                  className="box"
                  id="guests"
                  placeholder="Number of Guests"
                  value={formData.guests}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Budget Input Field */}
              <div className="input-field">
                <label htmlFor="budget"><FaDollarSign /></label>
                <input
                  type="number"
                  name="budget"
                  className="box"
                  id="budget"
                  placeholder="Estimated Budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Additional Notes Text Area */}
            <div className="input-field text-area">
              <label htmlFor="additionalNotes"><FaComment /></label>
              <textarea
                cols="30"
                rows="10"
                name="additionalNotes"
                className="box"
                id="additionalNotes"
                placeholder="Additional Notes or Special Requests"
                value={formData.additionalNotes}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Request Booking'}
            </button>

            {/* Error and Success Messages */}
            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}

            {mailSent && <div className="alert success"><span>Booking Request Submitted Successfully</span></div>}

          </form>
        </div>
      </div>
    </section>
  );
}

export default BookingForm;